import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSettings, updateSettings } from '@/lib/api/settings';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { refreshStore } from '@/lib/store';

interface FooterContact {
  phone: string;
  email: string;
  address: string;
  city: string;
  country: string;
  openingHours: string;
  whatsappNumber: string;
  whatsappMessage: string;
  instagram: string;
  tiktok: string;
  footerTagline: string;
  footerCopyright: string;
}

const DEFAULT_CONTACT: FooterContact = {
  phone: '+41 76 559 39 93',
  email: 'info@esteva.ch',
  address: 'Rruga Bylis',
  city: 'Tiranë 1001',
  country: 'Albania',
  openingHours: 'Mo–Fr: 08:00–20:00 | Sa: 09:00–16:00 | So: Geschlossen',
  whatsappNumber: '41765593993',
  whatsappMessage: 'Hallo, ich interessiere mich für eine Behandlung bei Esteva Clinic.',
  instagram: 'https://instagram.com/estevaclinic',
  tiktok: '',
  footerTagline: 'Schönheit, die überzeugt. Präzision, die begeistert.',
  footerCopyright: '© 2026 Esteva Clinic. All rights reserved.',
};

export default function FooterContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<FooterContact>(DEFAULT_CONTACT);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    void (async () => {
      try {
        const s = await getSettings();
        if (s) {
          setData({
            phone: s.phone,
            email: s.email,
            address: s.address,
            city: s.city,
            country: s.country,
            openingHours: s.opening_hours,
            whatsappNumber: s.whatsapp_number,
            whatsappMessage: s.whatsapp_message,
            instagram: s.instagram,
            tiktok: s.tiktok,
            footerTagline: s.footer_tagline,
            footerCopyright: s.footer_copyright,
          });
        }
      } catch { setData(DEFAULT_CONTACT); }
    })();
  }, [navigate]);

  const handleSave = async () => {
    try {
      await updateSettings({
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
        country: data.country,
        opening_hours: data.openingHours,
        whatsapp_number: data.whatsappNumber,
        whatsapp_message: data.whatsappMessage,
        instagram: data.instagram,
        tiktok: data.tiktok,
        footer_tagline: data.footerTagline,
        footer_copyright: data.footerCopyright,
      });
      await logActivityDb('footer_updated', 'Footer & Kontakt aktualisiert');
      await refreshStore();
      setSavedMsg(t('adm1.saved'));
    } catch {
      setSavedMsg(t('adm1.save_error_dot'));
    }
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const update = (key: keyof FooterContact, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  const contactFields = [
    { key: 'phone' as const, label: t('adm1.label_phone'), icon: 'ri-phone-line' },
    { key: 'email' as const, label: t('adm1.label_email'), icon: 'ri-mail-line' },
    { key: 'address' as const, label: t('adm1.label_address'), icon: 'ri-map-pin-line' },
    { key: 'city' as const, label: t('adm1.label_city_zip'), icon: 'ri-building-line' },
    { key: 'country' as const, label: t('adm1.label_country'), icon: 'ri-earth-line' },
    { key: 'openingHours' as const, label: t('adm1.label_opening_hours'), icon: 'ri-time-line' },
  ];

  const socialFields = [
    { key: 'instagram' as const, label: 'Instagram', icon: 'ri-instagram-line' },
    { key: 'tiktok' as const, label: 'TikTok', icon: 'ri-tiktok-line' },
  ];

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.footer_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">{t('adm1.footer_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm1.save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Contact Info ──────────────────────────── */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100">
            <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm1.footer_contact_data')}</h2>
            <p className="text-[10px] text-foreground-400 font-body mt-0.5">{t('adm1.footer_contact_data_desc')}</p>
          </div>
          <div className="p-5 space-y-4">
            {contactFields.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-xs font-label font-semibold text-foreground-700 mb-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${field.icon} text-foreground-400 text-xs`}></i>
                  </div>
                  {field.label}
                </label>
                {field.key === 'openingHours' ? (
                  <textarea
                    value={data[field.key]}
                    onChange={(e) => update(field.key, e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y"
                  />
                ) : (
                  <input
                    type="text"
                    value={data[field.key]}
                    onChange={(e) => update(field.key, e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Social Media ──────────────────────────── */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100">
            <h2 className="font-label text-sm font-semibold text-foreground-900">Social Media</h2>
            <p className="text-[10px] text-foreground-400 font-body mt-0.5">{t('adm1.footer_social_desc')}</p>
          </div>
          <div className="p-5 space-y-4">
            {socialFields.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-xs font-label font-semibold text-foreground-700 mb-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${field.icon} text-foreground-400 text-xs`}></i>
                  </div>
                  {field.label}
                </label>
                <input
                  type="text"
                  value={data[field.key]}
                  onChange={(e) => update(field.key, e.target.value)}
                  placeholder={field.key === 'tiktok' ? '#' : 'https://...'}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
                {field.key === 'tiktok' && (
                  <p className="mt-1 text-[10px] text-foreground-400 font-body">{t('adm1.footer_tiktok_note')}</p>
                )}
              </div>
            ))}

            {/* WhatsApp — Floating Button */}
            <div className="pt-4 border-t border-background-100">
              <h3 className="flex items-center gap-2 text-xs font-label font-semibold text-foreground-700 mb-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-foreground-400 text-xs"></i>
                </div>
                {t('adm1.footer_whatsapp_floating')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-foreground-400 font-body mb-1 block">{t('adm1.label_whatsapp_number')}</label>
                  <input
                    type="text"
                    value={data.whatsappNumber}
                    onChange={(e) => update('whatsappNumber', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground-400 font-body mb-1 block">{t('adm1.label_whatsapp_message')}</label>
                  <input
                    type="text"
                    value={data.whatsappMessage}
                    onChange={(e) => update('whatsappMessage', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer Text ─────────────────────────────── */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm1.footer_texts')}</h2>
          <p className="text-[10px] text-foreground-400 font-body mt-0.5">{t('adm1.footer_texts_desc')}</p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-label font-semibold text-foreground-700 mb-1.5 block">{t('adm1.label_tagline')}</label>
            <input
              type="text"
              value={data.footerTagline}
              onChange={(e) => update('footerTagline', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-label font-semibold text-foreground-700 mb-1.5 block">{t('adm1.label_copyright')}</label>
            <input
              type="text"
              value={data.footerCopyright}
              onChange={(e) => update('footerCopyright', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
            />
          </div>
        </div>
      </div>
    </div>
  );
}